import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { getDownloadUrl, detectPlatform } from '../lib/constants';
import { trackDownloadClick } from '../lib/analytics';
import AntivirusModal from './AntivirusModal';

const EASE = [0.32, 0.72, 0, 1] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 28, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.7, ease: EASE },
  },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

export default function BottomCTA() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackDownloadClick(detectPlatform());
    if (detectPlatform() === 'win_x64') {
      setShowModal(true);
    } else {
      window.location.href = getDownloadUrl();
    }
  };

  const handleConfirm = () => {
    setShowModal(false);
    window.location.href = getDownloadUrl();
  };

  return (
    <section className="relative overflow-hidden py-24 md:py-32 bg-[#faf9f5] dark:bg-[#141413]">
      {/* Warm radial glow — mirrors Hero halo */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="glow-clay absolute left-1/2 top-1/2 h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 animate-float rounded-full opacity-60 blur-3xl" />
        <div className="glow-amber absolute right-[8%] bottom-[10%] h-[22rem] w-[22rem] animate-float-slow rounded-full opacity-40 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          {/* eyebrow */}
          <motion.div variants={fadeUp} className="mb-7 flex justify-center">
            <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
              {t('bottomCta.title')}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h2
            variants={fadeUp}
            className="font-display display-tight text-4xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-6xl"
          >
            {t('bottomCta.description')}
          </motion.h2>

          {/* CTA — button-in-button pattern matching Hero */}
          <motion.div variants={fadeUp} className="mt-12 flex justify-center">
            <a
              href={getDownloadUrl()}
              onClick={handleDownloadClick}
              className="group inline-flex items-center gap-3 rounded-full bg-[#ae5630] py-2 pl-7 pr-2 text-base font-medium text-white shadow-warm-lg transition-all duration-300 ease-spring hover:bg-[#c4633a] active:scale-[0.98]"
            >
              {t('bottomCta.downloadBtn')}
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition-transform duration-300 ease-spring group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </motion.div>
        </motion.div>
      </div>

      <AntivirusModal
        open={showModal}
        onConfirm={handleConfirm}
        onClose={() => setShowModal(false)}
      />
    </section>
  );
}
